import React, { useState, useEffect, useRef } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, FileText, Briefcase, Users, Settings, Search, Bell, LogOut, Plus, HardHat, Plane, KeyRound,
} from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Badge } from "../components/ui/badge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { deadlineStyle, deadlineLabel, STATI_GARA, STATI_COMMESSA } from "../lib/helpers";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard-link", end: true },
  { to: "/gare", label: "Gare", icon: FileText, testid: "nav-gare-link" },
  { to: "/commesse", label: "Commesse", icon: Briefcase, testid: "nav-commesse-link" },
];

function GlobalSearch() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const timer = useRef(null);

  useEffect(() => {
    clearTimeout(timer.current);
    if (q.trim().length < 2) { setResults(null); return; }
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q } });
        setResults(data);
      } catch (e) { /* noop */ }
    }, 300);
    return () => clearTimeout(timer.current);
  }, [q]);

  const go = (path) => { setOpen(false); setQ(""); setResults(null); navigate(path); };
  const empty = results && !results.gare.length && !results.commesse.length && !results.dipendenti.length;

  return (
    <div className="relative w-96">
      <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
      <input
        data-testid="global-search-input"
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        placeholder="Cerca gare, commesse, dipendenti…"
        className="w-full pl-9 pr-3 py-2 text-sm bg-slate-100 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-900/30 focus:bg-white transition-colors"
      />
      {open && results && (
        <div data-testid="global-search-results" className="absolute mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg z-50 max-h-96 overflow-auto text-sm">
          {empty && <div className="px-4 py-3 text-slate-500">Nessun risultato</div>}
          {results.gare.length > 0 && (
            <div>
              <div className="px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-slate-400">Gare</div>
              {results.gare.map((g) => (
                <button key={g.id} data-testid={`search-gara-${g.id}`} onMouseDown={() => go(`/gare?q=${encodeURIComponent(q)}`)}
                  className="w-full text-left px-4 py-2 hover:bg-slate-50">
                  <div className="font-medium text-slate-800 truncate">{g.denominazione}</div>
                  <div className="text-xs text-slate-500">{g.ente_banditore}</div>
                </button>
              ))}
            </div>
          )}
          {results.commesse.length > 0 && (
            <div>
              <div className="px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-slate-400">Commesse</div>
              {results.commesse.map((c) => (
                <button key={c.id} data-testid={`search-commessa-${c.id}`} onMouseDown={() => go(`/commesse?q=${encodeURIComponent(q)}`)}
                  className="w-full text-left px-4 py-2 hover:bg-slate-50">
                  <div className="font-medium text-slate-800 truncate">{c.codice} — {c.denominazione}</div>
                  <div className="text-xs text-slate-500">{c.committente}</div>
                </button>
              ))}
            </div>
          )}
          {results.dipendenti.length > 0 && (
            <div>
              <div className="px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-slate-400">Dipendenti</div>
              {results.dipendenti.map((d) => (
                <button key={d.id} data-testid={`search-dipendente-${d.id}`} onMouseDown={() => go(`/dipendenti?sel=${d.id}`)}
                  className="w-full text-left px-4 py-2 hover:bg-slate-50">
                  <div className="font-medium text-slate-800">{d.nome} {d.cognome}</div>
                  <div className="text-xs text-slate-500">{d.ruolo}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function NotificationBell() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const res = await api.get("/notifiche");
      setData(res.data);
    } catch (e) { /* noop */ }
  };

  useEffect(() => { load(); }, []);

  const critici = data
    ? data.gare.filter((g) => g.giorni_rimanenti <= 7).length + data.commesse.filter((c) => c.giorni_rimanenti <= 7).length
    : 0;

  return (
    <DropdownMenu open={open} onOpenChange={(v) => { setOpen(v); if (v) load(); }}>
      <DropdownMenuTrigger asChild>
        <button data-testid="notification-trigger-button" className="relative p-2 rounded-md hover:bg-slate-100 transition-colors">
          <Bell className="h-5 w-5 text-slate-600" />
          {critici > 0 && (
            <span data-testid="notification-count-badge" className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-0.5 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center">
              {critici}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-96 max-h-96 overflow-auto">
        <div className="px-3 py-2 text-sm font-semibold text-slate-800">Scadenze imminenti</div>
        <DropdownMenuSeparator />
        {!data && <div className="px-3 py-3 text-sm text-slate-500">Caricamento…</div>}
        {data && data.gare.length === 0 && data.commesse.length === 0 && (
          <div className="px-3 py-3 text-sm text-slate-500">Nessuna scadenza imminente.</div>
        )}
        {data?.gare.map((g) => {
          const st = deadlineStyle(g.giorni_rimanenti);
          return (
            <DropdownMenuItem key={g.id} data-testid={`notif-gara-${g.id}`} onSelect={() => navigate("/gare")} className="flex items-start gap-2 cursor-pointer">
              <Badge variant="outline" className={`${st.badge} font-code shrink-0`}>{deadlineLabel(g.giorni_rimanenti)}</Badge>
              <div className="min-w-0">
                <div className="text-xs font-medium text-slate-800 truncate">{g.denominazione}</div>
                <div className="text-[11px] text-slate-500">Gara · {STATI_GARA[g.stato]}</div>
              </div>
            </DropdownMenuItem>
          );
        })}
        {data?.commesse.map((c) => {
          const st = deadlineStyle(c.giorni_rimanenti);
          return (
            <DropdownMenuItem key={c.id} data-testid={`notif-commessa-${c.id}`} onSelect={() => navigate("/commesse")} className="flex items-start gap-2 cursor-pointer">
              <Badge variant="outline" className={`${st.badge} font-code shrink-0`}>{deadlineLabel(c.giorni_rimanenti)}</Badge>
              <div className="min-w-0">
                <div className="text-xs font-medium text-slate-800 truncate">{c.codice} — {c.denominazione}</div>
                <div className="text-[11px] text-slate-500">Commessa · {STATI_COMMESSA[c.stato]}</div>
              </div>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function CambiaPasswordDialog({ open, onClose }) {
  const [vecchia, setVecchia] = useState("");
  const [nuova, setNuova] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setVecchia(""); setNuova(""); } }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/auth/cambia-password", { vecchia_password: vecchia, nuova_password: nuova });
      toast.success("Password aggiornata correttamente");
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-900/30";
  const labelCls = "block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1";

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm" data-testid="cambia-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Cambia password</DialogTitle>
          <DialogDescription>Aggiorna la password del tuo account. Le altre sessioni attive verranno disconnesse.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className={labelCls}>Password attuale</label>
            <input type="password" required data-testid="cambia-password-vecchia" value={vecchia} onChange={(e) => setVecchia(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Nuova password (min. 8 caratteri)</label>
            <input type="password" required minLength={8} data-testid="cambia-password-nuova" value={nuova} onChange={(e) => setNuova(e.target.value)} className={inputCls} />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} data-testid="cambia-password-cancel" className="px-4 py-2 text-sm font-medium border border-slate-300 rounded-md hover:bg-slate-50">Annulla</button>
            <button type="submit" disabled={saving} data-testid="cambia-password-submit" className="px-4 py-2 text-sm font-semibold bg-blue-900 text-white rounded-md hover:bg-blue-950 disabled:opacity-60">
              {saving ? "Salvataggio…" : "Aggiorna password"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const [pwdOpen, setPwdOpen] = useState(false);

  return (
    <div className="min-h-screen flex" data-testid="app-layout">
      <aside className="w-64 shrink-0 bg-slate-900 text-slate-100 flex flex-col fixed inset-y-0 z-30">
        <div className="px-5 py-5 border-b border-slate-800 flex items-center gap-3">
          <div className="h-9 w-9 rounded-md bg-blue-800 flex items-center justify-center">
            <HardHat className="h-5 w-5 text-white" />
          </div>
          <div>
            <div className="font-display font-bold text-sm leading-tight">StudioNex</div>
            <div className="text-[11px] text-slate-400 font-code">Gestionale 2026</div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {NAV.map(({ to, label, icon: Icon, testid, end }) => (
            <NavLink key={to} to={to} end={end} data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  isActive ? "bg-blue-800 text-white" : "text-slate-300 hover:bg-slate-800 hover:text-white"}`
              }>
              <Icon className="h-4 w-4" />
              {label}
            </NavLink>
          ))}
          {isAdmin && (
            <NavLink to="/dipendenti" data-testid="nav-dipendenti-link"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  isActive ? "bg-blue-800 text-white" : "text-slate-300 hover:bg-slate-800 hover:text-white"}`
              }>
              <Users className="h-4 w-4" />
              Dipendenti e Collaboratori
            </NavLink>
          )}
          {isAdmin && (
            <NavLink to="/trasferte" data-testid="nav-trasferte-link"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  isActive ? "bg-blue-800 text-white" : "text-slate-300 hover:bg-slate-800 hover:text-white"}`
              }>
              <Plane className="h-4 w-4" />
              Trasferte
            </NavLink>
          )}
          {isAdmin && (
            <NavLink to="/impostazioni" data-testid="nav-impostazioni-link"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  isActive ? "bg-blue-800 text-white" : "text-slate-300 hover:bg-slate-800 hover:text-white"}`
              }>
              <Settings className="h-4 w-4" />
              Impostazioni
            </NavLink>
          )}
        </nav>
        <div className="px-5 py-4 border-t border-slate-800 text-[11px] text-slate-500">
          Accesso multi-postazione · Dati centralizzati
        </div>
      </aside>

      <div className="flex-1 ml-64 flex flex-col min-w-0">
        <header className="bg-white/90 backdrop-blur-md border-b border-slate-200 px-6 py-3 flex items-center justify-between sticky top-0 z-40">
          <GlobalSearch />
          <div className="flex items-center gap-2">
            <button data-testid="quick-new-gara" onClick={() => navigate("/gare?nuova=1")}
              className="hidden xl:flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors">
              <Plus className="h-3.5 w-3.5" /> Gara
            </button>
            <button data-testid="quick-new-commessa" onClick={() => navigate("/commesse?nuova=1")}
              className="hidden xl:flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md bg-blue-900 text-white hover:bg-blue-950 transition-colors">
              <Plus className="h-3.5 w-3.5" /> Commessa
            </button>
            <NotificationBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-trigger" className="flex items-center gap-2.5 pl-2 pr-1 py-1 rounded-md hover:bg-slate-100 transition-colors">
                  <div className="text-right">
                    <div className="text-sm font-semibold text-slate-800 leading-tight">{user?.name}</div>
                    <div className="text-[11px] text-slate-500">{isAdmin ? "Amministratore" : "Operatore"}</div>
                  </div>
                  <div className="h-8 w-8 rounded-full bg-slate-800 text-white flex items-center justify-center text-xs font-bold">
                    {(user?.name || "U").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <div className="px-3 py-2">
                  <Badge data-testid="role-badge" variant="outline" className={isAdmin ? "bg-blue-50 text-blue-800 border-blue-200" : "bg-slate-100 text-slate-700 border-slate-200"}>
                    {isAdmin ? "Amministratore" : "Operatore"}
                  </Badge>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="cambia-password-trigger" onSelect={() => setPwdOpen(true)} className="cursor-pointer">
                  <KeyRound className="h-4 w-4 mr-2" /> Cambia password
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="logout-button" onSelect={async () => { await logout(); navigate("/login"); }} className="cursor-pointer text-red-600">
                  <LogOut className="h-4 w-4 mr-2" /> Esci
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <CambiaPasswordDialog open={pwdOpen} onClose={() => setPwdOpen(false)} />
        <main className="flex-1 p-6 min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
